// Catalog.ID Client — Shell Configuration
// ============================================================================

import type { ShellConfig, GridLayoutConfig, FlowLayoutConfig, WorkspaceDefinition } from '@catalog-shell';
import { isDashboardContext } from './state/dashboard-context';

// ============================================================================
// Pane type IDs (referenced by layouts and pane type registration)
// ============================================================================

export const PANE_IDS = {
    dashboard: 'cid-dashboard',
    claimsList: 'cid-claims-list',
    claimDetail: 'cid-claim-detail',
    sharing: 'cid-sharing',
    sharedWithMe: 'cid-shared-with-me',
    publicClaims: 'cid-public-claims',
    publicProfile: 'cid-public-profile',
    delegatesList: 'cid-delegates-list',
    delegateDetail: 'cid-delegate-detail',
    orgClaims: 'cid-org-claims',
    /** Organization recovery-kit status (registration-restructure §8 — the
     *  org-scoped twin of the personal kit record in Settings). */
    orgRecovery: 'cid-org-recovery',
    /** The organization's delegate-voucher ledger: buy slots, see what became of
     *  each (registration-restructure §13.4). Owner/admin. */
    orgVouchers: 'cid-org-vouchers',
    /** The organization's bitcash wallet: balance, receive address, spending
     *  permissions, per-delegate tokens (registration-wallets §4.13). On every
     *  delegate's roster — the key belongs to the owner and the TREASURER, and
     *  a treasurer is not an org-MANAGING role, so the pane carries its own
     *  one-line gate instead of being hidden from the roster. */
    orgWallets: 'cid-org-wallets',
    agentsList: 'cid-agents-list',
    agentDetail: 'cid-agent-detail',
    devices: 'cid-devices',
    // Account chat-root publication ceremony (chat migration §14 M3). Lives in
    // Settings beside Devices on purpose: Devices is the per-MACHINE key
    // surface, this is the per-ACCOUNT one, and §11's two ceremonies are the
    // pair. Also reachable at the `/chat-root` deep link.
    chatRoot: 'cid-chat-root',
    settings: 'cid-settings',
    connectedApps: 'cid-connected-apps',
    connectedModules: 'cid-connected-modules',
    sharedClaimView: 'cid-shared-claim-view',
    agentApiKeys: 'cid-agent-api-keys',
    agentTokens: 'cid-agent-tokens',
    operatorReview: 'cid-operator-review',
    operatorReviewDetail: 'cid-operator-review-detail',
    /** Registration voucher backoffice — mint / list / revoke (registration-restructure §7.1). */
    vouchers: 'cid-vouchers',
    unlockRequests: 'cid-unlock-requests',
    unlockRequestDetail: 'cid-unlock-request-detail',
    publicClaimDetail: 'cid-public-claim-detail',
    trustGiven: 'cid-trust-given',
    trustDetail: 'cid-trust-detail',
    trustReceived: 'cid-trust-received',
    trustExpress: 'cid-trust-express',
    // Payload-summoned ceremony console. Deliberately absent from every
    // workspace layout and recommendedPanes list — entry is the documented
    // `/ceremony` deep link only (docs/operator-org-binding.md §6 Q1).
    ceremonyConsole: 'cid-ceremony-console',
} as const;

// ============================================================================
// Workspace: Home
// ============================================================================

const homePanes = [PANE_IDS.dashboard, PANE_IDS.publicProfile];

const homeFullLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 2,
    columnSizes: [60, 40],
    rowSizes: [100],
    configuredPaneTypes: homePanes,
    cells: [
        { cellId: 'left', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.dashboard }], activePaneIndex: 0 },
        { cellId: 'right', rowStart: 0, rowEnd: 1, colStart: 1, colEnd: 2, panes: [{ typeId: PANE_IDS.publicProfile }], activePaneIndex: 0 },
    ],
};

const homeCompactLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 1,
    columnSizes: [100],
    rowSizes: [100],
    configuredPaneTypes: homePanes,
    cells: [
        { cellId: 'main', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.dashboard }], activePaneIndex: 0 },
    ],
};

const homeWorkspace: WorkspaceDefinition = {
    id: 'home',
    label: 'Home',
    enabledSizes: ['compact', 'medium', 'full'],
    rootPaneSpec: { typeId: 'grid-surface', title: 'Home' },
    layouts: {
        compact: homeCompactLayout,
        medium: homeFullLayout,
        full: homeFullLayout,
    },
    recommendedPanes: homePanes,
};

// ============================================================================
// Workspace: Claims (FlowSurface)
// ============================================================================

const claimsLayout: FlowLayoutConfig = {
    type: 'flow',
    panes: [{ paneSpec: { typeId: PANE_IDS.claimsList, title: 'Claims' }, width: 450 }],
};

const claimsWorkspace: WorkspaceDefinition = {
    id: 'claims',
    label: 'Claims',
    enabledSizes: ['compact', 'medium', 'full'],
    rootPaneSpec: { typeId: 'flow-surface', title: 'Claims' },
    rootLayoutConfig: claimsLayout,
};

// ============================================================================
// Workspace: Organization (GridSurface — claims + delegates)
// ============================================================================

/**
 * Organization panes that only an OWNER or ADMIN delegate can use — the server
 * refuses every call behind them for other roles (delegates list/detail,
 * the voucher ledger, the recovery kit). A viewer or treasurer gets an
 * organization workspace without them rather than three panes that answer
 * 403 (decided 2026-09-04 after the M15 live test).
 */
export const ORG_ROLE_GATED_PANES: readonly string[] = [
    PANE_IDS.delegatesList, PANE_IDS.delegateDetail, PANE_IDS.orgVouchers, PANE_IDS.orgRecovery,
];

const orgPanesAll = [
    PANE_IDS.orgClaims, PANE_IDS.orgRecovery, PANE_IDS.orgVouchers, PANE_IDS.orgWallets,
    PANE_IDS.delegatesList, PANE_IDS.delegateDetail,
];

export function orgRolesCanManage(roles: readonly string[]): boolean {
    return roles.includes('owner') || roles.includes('admin');
}

/** The organization workspace's pane roster (`configuredPaneTypes`) for a delegate's roles. */
export function orgPanesForRoles(roles: readonly string[]): string[] {
    return orgRolesCanManage(roles)
        ? [...orgPanesAll]
        : orgPanesAll.filter(id => !ORG_ROLE_GATED_PANES.includes(id));
}

function buildOrgLayouts(roles: readonly string[]): { full: GridLayoutConfig; compact: GridLayoutConfig } {
    const configuredPaneTypes = orgPanesForRoles(roles);
    if (!orgRolesCanManage(roles)) {
        const single: GridLayoutConfig = {
            type: 'grid',
            columnCount: 1,
            columnSizes: [100],
            rowSizes: [100],
            configuredPaneTypes,
            cells: [
                { cellId: 'main', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.orgClaims }, { typeId: PANE_IDS.orgWallets }], activePaneIndex: 0 },
            ],
        };
        return { full: single, compact: single };
    }
    const full: GridLayoutConfig = {
        type: 'grid',
        columnCount: 2,
        columnSizes: [50, 50],
        rowSizes: [100],
        configuredPaneTypes,
        cells: [
            { cellId: 'left', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.orgClaims }, { typeId: PANE_IDS.orgRecovery }], activePaneIndex: 0 },
            { cellId: 'right', rowStart: 0, rowEnd: 1, colStart: 1, colEnd: 2, panes: [{ typeId: PANE_IDS.delegatesList }, { typeId: PANE_IDS.orgVouchers }, { typeId: PANE_IDS.orgWallets }], activePaneIndex: 0 },
        ],
    };
    const compact: GridLayoutConfig = {
        type: 'grid',
        columnCount: 1,
        columnSizes: [100],
        rowSizes: [100],
        configuredPaneTypes,
        cells: [
            { cellId: 'main', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.orgClaims }, { typeId: PANE_IDS.orgRecovery }, { typeId: PANE_IDS.delegatesList }, { typeId: PANE_IDS.orgVouchers }, { typeId: PANE_IDS.orgWallets }], activePaneIndex: 0 },
        ],
    };
    return { full, compact };
}

/** The organization workspace definition for a delegate's roles (see ORG_ROLE_GATED_PANES). */
export function buildOrgWorkspace(roles: readonly string[]): WorkspaceDefinition {
    const layouts = buildOrgLayouts(roles);
    return {
        id: 'organization',
        label: 'Organization',
        enabledSizes: ['compact', 'medium', 'full'],
        rootPaneSpec: { typeId: 'grid-surface', title: 'Organization' },
        layouts: {
            compact: layouts.compact,
            medium: layouts.full,
            full: layouts.full,
        },
    };
}

const orgWorkspace: WorkspaceDefinition = buildOrgWorkspace(['owner']);

// ============================================================================
// Workspace: Agents (FlowSurface)
// ============================================================================

const agentsLayout: FlowLayoutConfig = {
    type: 'flow',
    panes: [{ paneSpec: { typeId: PANE_IDS.agentsList, title: 'Agents' }, width: 450 }],
};

const agentsWorkspace: WorkspaceDefinition = {
    id: 'agents',
    label: 'Agents',
    enabledSizes: ['medium', 'full'],
    rootPaneSpec: { typeId: 'flow-surface', title: 'Agents' },
    rootLayoutConfig: agentsLayout,
};

// ============================================================================
// Workspace: Trust (GridSurface — given list + received summary)
// ============================================================================

const trustPanes = [PANE_IDS.trustGiven, PANE_IDS.trustReceived];

const trustFullLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 2,
    columnSizes: [55, 45],
    rowSizes: [100],
    configuredPaneTypes: trustPanes,
    cells: [
        { cellId: 'left', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.trustGiven }], activePaneIndex: 0 },
        { cellId: 'right', rowStart: 0, rowEnd: 1, colStart: 1, colEnd: 2, panes: [{ typeId: PANE_IDS.trustReceived }], activePaneIndex: 0 },
    ],
};

const trustCompactLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 1,
    columnSizes: [100],
    rowSizes: [100],
    configuredPaneTypes: trustPanes,
    cells: [
        { cellId: 'main', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.trustGiven }, { typeId: PANE_IDS.trustReceived }], activePaneIndex: 0 },
    ],
};

const trustWorkspace: WorkspaceDefinition = {
    id: 'trust',
    label: 'Trust',
    enabledSizes: ['compact', 'medium', 'full'],
    rootPaneSpec: { typeId: 'grid-surface', title: 'Trust' },
    layouts: {
        compact: trustCompactLayout,
        medium: trustFullLayout,
        full: trustFullLayout,
    },
    recommendedPanes: trustPanes,
};

// ============================================================================
// Workspace: Operator (GridSurface — publication review + unlock requests)
// ============================================================================

const operatorPanes = [
    PANE_IDS.operatorReview, PANE_IDS.operatorReviewDetail,
    PANE_IDS.unlockRequests, PANE_IDS.unlockRequestDetail,
    PANE_IDS.vouchers,
];

const operatorFullLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 2,
    columnSizes: [50, 50],
    rowSizes: [100],
    configuredPaneTypes: operatorPanes,
    cells: [
        { cellId: 'left', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.operatorReview }], activePaneIndex: 0 },
        { cellId: 'right', rowStart: 0, rowEnd: 1, colStart: 1, colEnd: 2, panes: [{ typeId: PANE_IDS.unlockRequests }, { typeId: PANE_IDS.vouchers }], activePaneIndex: 0 },
    ],
};

const operatorCompactLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 1,
    columnSizes: [100],
    rowSizes: [100],
    configuredPaneTypes: operatorPanes,
    cells: [
        { cellId: 'main', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.operatorReview }, { typeId: PANE_IDS.unlockRequests }, { typeId: PANE_IDS.vouchers }], activePaneIndex: 0 },
    ],
};

const operatorWorkspace: WorkspaceDefinition = {
    id: 'operator',
    label: 'Operator',
    enabledSizes: ['compact', 'medium', 'full'],
    rootPaneSpec: { typeId: 'grid-surface', title: 'Operator' },
    layouts: {
        compact: operatorCompactLayout,
        medium: operatorFullLayout,
        full: operatorFullLayout,
    },
    recommendedPanes: operatorPanes,
};

// ============================================================================
// Workspace: Settings (GridSurface)
// ============================================================================

// Connected Modules is only meaningful when running as a dashboard module —
// the standalone catalog.id site has no local cross-module Key Grant traffic.
const rightColumnPanes = isDashboardContext()
    ? [{ typeId: PANE_IDS.connectedApps }, { typeId: PANE_IDS.connectedModules }]
    : [{ typeId: PANE_IDS.connectedApps }];

const settingsPanes = isDashboardContext()
    ? [PANE_IDS.settings, PANE_IDS.devices, PANE_IDS.chatRoot, PANE_IDS.connectedApps, PANE_IDS.connectedModules]
    : [PANE_IDS.settings, PANE_IDS.devices, PANE_IDS.chatRoot, PANE_IDS.connectedApps];

const settingsFullLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 2,
    columnSizes: [50, 50],
    rowSizes: [100],
    configuredPaneTypes: settingsPanes,
    cells: [
        { cellId: 'left', rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1, panes: [{ typeId: PANE_IDS.settings }, { typeId: PANE_IDS.devices }, { typeId: PANE_IDS.chatRoot }], activePaneIndex: 0 },
        { cellId: 'right', rowStart: 0, rowEnd: 1, colStart: 1, colEnd: 2, panes: rightColumnPanes, activePaneIndex: 0 },
    ],
};

const settingsCompactLayout: GridLayoutConfig = {
    type: 'grid',
    columnCount: 1,
    columnSizes: [100],
    rowSizes: [100],
    configuredPaneTypes: settingsPanes,
    cells: [
        {
            cellId: 'main',
            rowStart: 0, rowEnd: 1, colStart: 0, colEnd: 1,
            panes: settingsPanes.map((typeId) => ({ typeId })),
            activePaneIndex: 0,
        },
    ],
};

const settingsWorkspace: WorkspaceDefinition = {
    id: 'settings',
    label: 'Settings',
    enabledSizes: ['compact', 'medium', 'full'],
    rootPaneSpec: { typeId: 'grid-surface', title: 'Settings' },
    layouts: {
        compact: settingsCompactLayout,
        medium: settingsFullLayout,
        full: settingsFullLayout,
    },
    recommendedPanes: settingsPanes,
};

// ============================================================================
// Shell Config
// ============================================================================

/**
 * The shell configuration for the signed-in identity. The organization
 * workspace's roster depends on the delegate's roles (ORG_ROLE_GATED_PANES);
 * everything else is static. The shell is re-created on every identity
 * switch, so a role change is picked up there.
 */
export function buildShellConfig(opts: { orgRoles: readonly string[] }): ShellConfig {
    return {
        ...shellConfig,
        workspaces: shellConfig.workspaces.map(w => w.id === 'organization' ? buildOrgWorkspace(opts.orgRoles) : w),
    };
}

/** Static configuration with the full (owner) organization roster — prefer buildShellConfig(). */
export const shellConfig: ShellConfig = {
    workspaces: [
        homeWorkspace,
        claimsWorkspace,
        orgWorkspace,
        agentsWorkspace,
        trustWorkspace,
        operatorWorkspace,
        settingsWorkspace,
    ],
    defaultWorkspace: 'home',
    // Bumped 1 → 2 when the Settings workspace gained the Chat Identity pane
    // (chat migration §14 M3); 2 → 3 when the Operator workspace gained the
    // voucher backoffice (registration-restructure M7); 3 → 4 when the
    // Organization workspace gained the Recovery Kit pane; 4 → 5 when it gained
    // the delegate-voucher ledger (registration-restructure M14); 5 → 6 when it
    // gained the Wallets pane (registration-wallets §4.13).
    // `Shell.loadConfiguration` discards a persisted layout whose configVersion
    // differs, which is the only thing that makes a new pane appear for a
    // browser that already has a stored layout.
    configVersion: 6,
};
